import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Hero } from './components/Hero';
import { TheMirror } from './components/TheMirror';
import { Projects } from './components/Projects';
import { Experience } from './components/Experience';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { Testimonials } from './components/Testimonials';
import { Terms } from './pages/Terms';
import { Cookies } from './pages/Cookies';
import { Privacy } from './pages/Privacy';
import { AboutIntro } from './components/AboutIntro';
import { ToolShowcase } from './components/ToolShowcase';
import AIChat from './components/AIChat';
import { CookieConsent } from './components/CookieConsent';
import { AnalyticsScripts } from './components/AnalyticsScripts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SEO from './components/SEO';
import { Toaster } from 'react-hot-toast';
import { trackPageView } from './lib/analytics';
import './i18n/config';
import { Login } from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import DemoLogin from './pages/DemoLogin';
import { ThemeProvider } from './context/ThemeContext';

// Lazy-loaded pages (heavy deps: chart.js, xlsx, leaflet, CRM tables)
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Solutions = lazy(() => import('./pages/Solutions').then(m => ({ default: m.Solutions })));
const Vector = lazy(() => import('./pages/Vector').then(m => ({ default: m.Vector })));
const CRMPage = lazy(() => import('./pages/crm/CRMPage').then(m => ({ default: m.CRMPage })));

const PageFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
  </div>
);
const queryClient = new QueryClient();

// Route change tracking component
function RouteChangeTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname);
  }, [location]);
  
  return null;
}

function RecoveryRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const hash = window.location.hash;
    if (hash && /type=(recovery|invite|signup)/.test(hash) && location.pathname !== '/reset-password') {
      navigate(`/reset-password${hash}`);
    }
  }, [location, navigate]);

  return null;
}

function HomePage() {
  return (
    <>
      <SEO
        title="Victor Lazari – Founder & Chief Architect"
        description="Victor Lazari, Founder & Architetto di Varienda-BMS. Progetto sistemi AI e automazione che trasformano il caos operativo in vantaggio competitivo. Soluzioni ad alto impatto per il controllo e la crescita."
        canonical="https://victorlazari.com/"
        image="https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"
        type="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Victor Lazari',
          url: 'https://victorlazari.com/',
          jobTitle: 'Founder & Chief Architect'
        }}
      />
      <Hero />
      <TheMirror />
      <Projects />
      <Experience />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isInCRM = location.pathname.startsWith('/crm');

  // Prevent body scroll when in CRM
  useEffect(() => {
    if (isInCRM) {
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalHeight = window.getComputedStyle(document.body).height;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = '';
        document.body.style.height = originalHeight;
      };
    }
  }, [isInCRM]);

  return (
    <div className="relative min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
      <div className="relative z-10 flex-1">
        <RouteChangeTracker />
        <RecoveryRedirect />
        {!isInCRM && <Navigation />}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/blog" element={<Suspense fallback={<PageFallback />}><Blog /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={<PageFallback />}><Blog /></Suspense>} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/contact" element={<Suspense fallback={<PageFallback />}><Contact /></Suspense>} />
          <Route path="/solutions" element={<Suspense fallback={<PageFallback />}><Solutions /></Suspense>} />
          <Route path="/demo-login" element={<DemoLogin />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={
            <>
              <AboutIntro />
              <ToolShowcase />
            </>
          } />
          <Route path="/vector" element={<Suspense fallback={<PageFallback />}><Vector /></Suspense>} />
          <Route path="/crm/*" element={<Suspense fallback={<PageFallback />}><CRMPage /></Suspense>} />
        </Routes>
      </div>
      {!isInCRM && (
        <div className="relative z-10">
          <Footer />
        </div>
      )}
      <AIChat />
      <CookieConsent />
      <AnalyticsScripts />
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--toast-bg, #ffffff)',
            color: 'var(--toast-color, #111827)',
            border: 'var(--toast-border, 1px solid #e5e7eb)',
          },
          className: 'dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700',
        }}
      />
    </div>
  );
}

export default App;